<template>
  <b-card class="border rounded-xl border-gray-200 mb-3">
    <!-- Header -->
    <div class="flex items-center justify-between pb-1 mb-2 border-b">
      <div>
        <h5 class="text-base font-semibold text-[#333333] mb-0.5">
          Human Handover
        </h5>
        <p class="text-xs md:text-sm text-[#828282] mb-0">
          Atur kapan AI mengalihkan percakapan ke agen dan kapan AI kembali menangani percakapan.
        </p>
      </div>
      <b-form-checkbox
        id="toggle-human-handover"
        v-model="handoverData.is_active"
        switch
        class="custom-cs-switch mb-0"
      />
    </div>

    <!-- Body (Visible when Human Handover is enabled) -->
    <div
      v-show="handoverData.is_active"
      class="mt-2 space-y-3"
    >
      <!-- Section 1: Alihkan ke Agen -->
      <div>
        <div class="text-sm font-medium text-[#333333] mb-1">
          Alihkan ke Agen<span class="text-danger">*</span>
        </div>

        <div class="border border-gray-200 rounded-lg p-2 bg-white space-y-3">
          <!-- Item 1: Keyword Trigger -->
          <div>
            <div class="flex items-center gap-1">
              <b-form-checkbox
                id="checkbox-keyword-trigger"
                v-model="handoverData.is_keyword_trigger"
                class="custom-cs-checkbox mb-0"
              >
                <span class="text-sm font-medium text-[#1E293B]">Keyword Trigger</span>
              </b-form-checkbox>
              <span
                id="popover-keyword-trigger"
                class="cursor-pointer inline-flex items-center text-[#F97316]"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                  />
                  <line
                    x1="12"
                    y1="16"
                    x2="12"
                    y2="12"
                  />
                  <line
                    x1="12"
                    y1="8"
                    x2="12.01"
                    y2="8"
                  />
                </svg>
              </span>
              <b-popover
                target="popover-keyword-trigger"
                triggers="hover"
                placement="top"
                custom-class="custom-popover text-xs"
              >
                Kata atau frasa yang memicu AI untuk mengalihkan percakapan ke CS manusia.
              </b-popover>
            </div>

            <!-- Tags Input Box (Active when Keyword Trigger checked) -->
            <div
              v-show="handoverData.is_keyword_trigger"
              class="mt-2"
            >
              <div
                class="border border-gray-200 rounded-lg p-1.5 bg-white min-h-[42px] flex flex-wrap items-center gap-1.5 focus-within:border-[#F97316] transition-colors"
                @click="focusInput"
              >
                <!-- Chips -->
                <span
                  v-for="(tag, index) in handoverData.keywords"
                  :key="index"
                  class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[#FFF7ED] text-[#F97316] border border-[#FFEDD5]"
                >
                  {{ tag }}
                  <button
                    type="button"
                    class="p-0 border-0 bg-transparent text-[#F97316] hover:text-red-600 cursor-pointer text-xs font-bold leading-none ml-0.5 focus:outline-none"
                    @click.stop="removeKeyword(index)"
                  >
                    &times;
                  </button>
                </span>

                <!-- Native Text Input -->
                <input
                  ref="tagInputRef"
                  v-model="newKeyword"
                  type="text"
                  placeholder="Ketik kata kunci lalu tekan Enter"
                  class="border-0 p-0 text-sm focus:outline-none flex-1 min-w-[160px] bg-transparent"
                  :disabled="totalCharacters >= 50 || (handoverData.keywords || []).length >= 20"
                  @keydown="onKeydownInput"
                >
              </div>

              <!-- Footer Info: Total Count & Character Limit -->
              <div class="flex items-center justify-between text-xs text-[#828282] mt-1 px-0.5">
                <span :class="{ 'text-danger font-medium': (handoverData.keywords || []).length >= 20 }">
                  {{ (handoverData.keywords || []).length }}/20 keyword digunakan
                </span>
                <span :class="{ 'text-danger font-medium': totalCharacters >= 50 }">
                  {{ totalCharacters }}/50 karakter
                </span>
              </div>
            </div>
          </div>

          <!-- Item 2: Negative Sentiment -->
          <div>
            <div class="flex items-center gap-1">
              <b-form-checkbox
                id="checkbox-negative-sentiment"
                v-model="handoverData.is_negative_sentiment"
                class="custom-cs-checkbox mb-0"
              >
                <span class="text-sm font-medium text-[#1E293B]">Negative Sentiment</span>
              </b-form-checkbox>
              <span
                id="popover-negative-sentiment"
                class="cursor-pointer inline-flex items-center text-[#F97316]"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                  />
                  <line
                    x1="12"
                    y1="16"
                    x2="12"
                    y2="12"
                  />
                  <line
                    x1="12"
                    y1="8"
                    x2="12.01"
                    y2="8"
                  />
                </svg>
              </span>
              <b-popover
                target="popover-negative-sentiment"
                triggers="hover"
                placement="top"
                custom-class="custom-popover text-xs"
              >
                Otomatis mengalihkan percakapan ke CS manusia saat AI mendeteksi sentimen negatif dari customer.
              </b-popover>
            </div>
          </div>
        </div>
      </div>

      <!-- Section 2: Kembali ke CS AI -->
      <div>
        <div class="text-sm font-medium text-[#333333] mb-1">
          Kembali ke CS AI<span class="text-danger">*</span>
        </div>

        <div class="border border-gray-200 rounded-lg p-2 bg-white">
          <div class="flex items-center gap-1">
            <b-form-checkbox
              id="checkbox-auto-return"
              v-model="handoverData.is_auto_return_cs_ai"
              class="custom-cs-checkbox mb-0"
            >
              <span class="text-sm font-medium text-[#1E293B]">Kembali ke CS AI Otomatis</span>
            </b-form-checkbox>
            <span
              id="popover-auto-return"
              class="cursor-pointer inline-flex items-center text-[#F97316]"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                />
                <line
                  x1="12"
                  y1="16"
                  x2="12"
                  y2="12"
                />
                <line
                  x1="12"
                  y1="8"
                  x2="12.01"
                  y2="8"
                />
              </svg>
            </span>
            <b-popover
              target="popover-auto-return"
              triggers="hover"
              placement="top"
              custom-class="custom-popover text-xs"
            >
              Otomatis mengembalikan percakapan ke AI setelah idle. Percakapan dengan eskalasi sentimen negatif harus dikembalikan secara manual oleh CS.
            </b-popover>
          </div>

          <!-- Minutes Input Box -->
          <div
            v-show="handoverData.is_auto_return_cs_ai"
            class="mt-2 max-w-[150px]"
          >
            <b-input-group
              append="menit"
              size="sm"
            >
              <b-form-input
                v-model.number="handoverData.auto_return_cs_ai_minutes"
                type="number"
                min="1"
                max="1440"
                placeholder="15"
              />
            </b-input-group>
            <span class="text-[11px] text-[#828282] mt-1 block">
              Durasi antara 1–1440.
            </span>
          </div>
        </div>
      </div>
    </div>
  </b-card>
</template>

<script>
export default {
  name: 'HumanHandover',
  props: {
    formData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      newKeyword: '',
    }
  },
  computed: {
    handoverData() {
      if (!this.formData.human_handover) {
        this.$set(this.formData, 'human_handover', {
          is_active: false,
          is_keyword_trigger: false,
          keywords: [],
          is_negative_sentiment: false,
          is_auto_return_cs_ai: false,
          auto_return_cs_ai_minutes: 15,
        })
      }
      return this.formData.human_handover
    },
    totalCharacters() {
      const keywords = this.handoverData.keywords || []
      return keywords.reduce((sum, tag) => sum + String(tag).length, 0)
    },
  },
  methods: {
    focusInput() {
      if (this.$refs.tagInputRef) {
        this.$refs.tagInputRef.focus()
      }
    },
    addKeyword() {
      const trimmed = String(this.newKeyword || '').trim().replace(/,/g, '')
      if (!trimmed) return

      if (!Array.isArray(this.handoverData.keywords)) {
        this.$set(this.handoverData, 'keywords', [])
      }

      // Check if keyword already exists
      if (this.handoverData.keywords.includes(trimmed)) {
        this.$toast_error({ message: 'Keyword sudah ditambahkan' })
        this.newKeyword = ''
        return
      }

      // Check max keyword limit (20)
      if (this.handoverData.keywords.length >= 20) {
        this.$toast_error({ message: 'Batas maksimal 20 keyword' })
        return
      }

      // Check if character limit (50) is reached
      if (this.totalCharacters + trimmed.length > 50) {
        this.$toast_error({ message: 'Total karakter keyword melebihi batas 50 karakter' })
        return
      }

      this.handoverData.keywords.push(trimmed)
      this.newKeyword = ''
    },
    removeKeyword(index) {
      if (Array.isArray(this.handoverData.keywords)) {
        this.handoverData.keywords.splice(index, 1)
      }
    },
    onKeydownInput(event) {
      if (event.key === 'Enter' || event.key === ',') {
        event.preventDefault()
        this.addKeyword()
      } else if (event.key === 'Backspace' && !this.newKeyword && Array.isArray(this.handoverData.keywords) && this.handoverData.keywords.length > 0) {
        this.handoverData.keywords.pop()
      }
    },
  },
}
</script>

<style lang="scss">
.custom-cs-switch {
  .custom-control-input:checked ~ .custom-control-label::before {
    border-color: #F97316 !important;
    background-color: #F97316 !important;
  }
}

.custom-cs-checkbox {
  .custom-control-label::before {
    border-radius: 4px !important;
    border: 1.5px solid #CBD5E1 !important;
  }
  .custom-control-input:checked ~ .custom-control-label::before {
    border-color: #F97316 !important;
    background-color: #F97316 !important;
  }
}
</style>
